import { useParams, useNavigate } from 'react-router-dom'
import { Container, Stack, Text, Loader, Center, Button, SimpleGrid, Divider } from '@mantine/core'
import { IconArrowLeft, IconTrendingUp } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { playerSessionService } from '../services/playerSessionService'
import { storyService } from '../services/storyService'
import { useProjectedProfile } from '../hooks/useProjectedProfile'
import { ProfileHeader } from '../components/projected-profile/ProfileHeader'
import { InputSummary } from '../components/projected-profile/InputSummary'
import { DimensionRadar } from '../components/projected-profile/DimensionRadar'
import { ArchetypeCard } from '../components/projected-profile/ArchetypeCard'
import { DeliverablesList } from '../components/projected-profile/DeliverablesList'
import { SkillGrowthTable } from '../components/projected-profile/SkillGrowthTable'
import { MentorAnalysis } from '../components/projected-profile/MentorAnalysis'

const EXPLORE_BASE_URL = 'https://bubotexplore.vercel.app'

export default function ProjectedProfile() {
  const { sessionId } = useParams()
  const navigate = useNavigate()

  // Fetch session to get story ID and thread ID
  const { data: session, isLoading: sessionLoading } = useQuery({
    queryKey: ['playerSession', sessionId],
    queryFn: () => playerSessionService.getPlayerSession(sessionId),
    enabled: !!sessionId,
  })

  // Fetch story
  const storyId = session?.story_id
  const { data: story, isLoading: storyLoading } = useQuery({
    queryKey: ['story', storyId],
    queryFn: () => storyService.getStory(storyId),
    enabled: !!storyId,
  })

  // Fetch beats — getBeats returns { data: [...], total, page, limit }
  const threadId = session?.current_thread_id
  const { data: beatsResponse, isLoading: beatsLoading } = useQuery({
    queryKey: ['beats', threadId],
    queryFn: () => storyService.getBeats(threadId, { limit: 50 }),
    enabled: !!threadId,
  })

  const beats = beatsResponse?.data || beatsResponse || []
  const { profile, hasConfig } = useProjectedProfile(sessionId, story, beats)
  const isLoading = sessionLoading || storyLoading || beatsLoading

  if (isLoading) {
    return (
      <Center style={{ height: '100vh' }}>
        <Stack align="center" gap="md">
          <Loader size="lg" color="cyan" />
          <Text c="dimmed" ff="Orbitron, sans-serif" size="sm">
            Building your projected profile...
          </Text>
        </Stack>
      </Center>
    )
  }

  if (!hasConfig || !profile) {
    return (
      <Container size="md" py="xl">
        <Stack align="center" gap="md" py={60}>
          <Text c="dimmed" size="sm">Projected profile not available for this story.</Text>
          <Button variant="light" color="gray" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/')}>
            Back to Stories
          </Button>
        </Stack>
      </Container>
    )
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate('/')}
          style={{ alignSelf: 'flex-start' }}
          size="sm"
        >
          Back to Stories
        </Button>

        <ProfileHeader
          programName={profile.programName}
          completedAt={profile.completedAt}
          duration={profile.sessionDuration}
        />

        {/* What the platform captured */}
        <InputSummary
          inputs={profile.learnerInputs}
          decisions={profile.learnerDecisions}
        />

        {/* Archetype from demo signals */}
        <ArchetypeCard archetype={profile.archetype} />

        {/* Assumptions callout */}
        <Stack gap={4} px="sm">
          <Text size="xs" c="dimmed" ff="Orbitron, sans-serif" fw={600} tt="uppercase" lts={1}>
            About This Projection
          </Text>
          <Text size="sm" c="gray.5" lh={1.5}>
            The profiles below project what the platform would deliver over a full program based on your demo responses and the pattern they indicate. In the live experience, every score updates dynamically based on real inputs — no two profiles look the same after Week 3.
          </Text>
        </Stack>

        <Divider color="rgba(0, 229, 255, 0.1)" />

        {/* ─── WEEK 12 PROFILE ─── */}
        <Text size="sm" fw={700} ff="Orbitron, sans-serif" c="cyan.4">
          {profile.sprint1.label} — {profile.sprint1.weeks}
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          <Stack gap="md" align="center">
            <DimensionRadar
              dimensions={profile.dimensionLabels}
              scores={profile.sprint1.scores}
              size={260}
            />
          </Stack>
          <DeliverablesList
            title="What You Built"
            weeks={profile.sprint1.weeks}
            deliverables={profile.sprint1.deliverables}
          />
        </SimpleGrid>

        <MentorAnalysis
          label={`COMPASS AI — ${profile.sprint1.weeks} Analysis`}
          narrative={profile.sprint1.narrative}
        />

        <Divider color="rgba(0, 229, 255, 0.1)" />

        {/* ─── WEEK 24 PROFILE ─── */}
        <Text size="sm" fw={700} ff="Orbitron, sans-serif" c="cyan.4">
          {profile.sprint2.label} — {profile.sprint2.weeks}
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          <Stack gap="md" align="center">
            <DimensionRadar
              dimensions={profile.dimensionLabels}
              scores={profile.sprint2.scores}
              ghostScores={profile.sprint1.scores}
              size={260}
            />
            <ArchetypeCard archetype={profile.archetype} evolved />
          </Stack>
          <Stack gap="md">
            <DeliverablesList
              title="Production Readiness"
              weeks={profile.sprint2.weeks}
              deliverables={profile.sprint2.deliverables}
            />
          </Stack>
        </SimpleGrid>

        <MentorAnalysis
          label={`COMPASS AI — ${profile.sprint2.weeks} Summary`}
          narrative={profile.sprint2.narrative}
        />

        <Divider color="rgba(0, 229, 255, 0.1)" />

        {/* ─── GROWTH TRAJECTORY ─── */}
        <SkillGrowthTable skills={profile.skillTrajectory} />

        <Divider color="rgba(0, 229, 255, 0.1)" />

        {/* ─── EXPLORE CTA ─── */}
        <Stack gap="sm" align="center" py="md">
          <Button
            component="a"
            href={`${EXPLORE_BASE_URL}/?session=${sessionId}&token=${(() => { try { return JSON.parse(localStorage.getItem('bw_authToken') || '') } catch { return '' } })()}`}
            target="_blank"
            variant="filled"
            color="lime"
            size="md"
            leftSection={<IconTrendingUp size={18} />}
            fullWidth
            maw={360}
          >
            Explore Your Growth Path
          </Button>
          <Text size="xs" c="dimmed" maw={320} ta="center">
            Set goals, adjust focus intensity, and see your projected growth over 12 weeks
          </Text>
        </Stack>

      </Stack>
    </Container>
  )
}
